import React from 'react';

import { Button, Group, Stack, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconPlus } from '@tabler/icons-react';
import PaymentMethodSelect from 'components/form-fields/auto-complete-fields/PaymentMethodSelect';
import SharedPaymentMethodsAlert from 'components/SharedPaymentMethodsAlert';
import { useAuth } from 'context/auth-context';
import useAthleteIsSharingPaymentMethods from 'hooks/athlete/useAthleteIsSharingPaymentMethods';

import CheckoutFlowStates from '../checkoutFlowStates';

interface FormValues {
  paymentMethod: string;
}

interface ISelectPaymentMethodProps {
  paymentMethod: string;
  setPaymentMethod: React.Dispatch<React.SetStateAction<string>>;
  setState: React.Dispatch<React.SetStateAction<CheckoutFlowStates>>;
  handleClose: () => void;
}

const SelectPaymentMethod = ({
  paymentMethod,
  setPaymentMethod,
  setState,
  handleClose,
}: ISelectPaymentMethodProps): JSX.Element => {
  const theme = useMantineTheme();
  const { session } = useAuth();
  const isSharingPaymentMethods = useAthleteIsSharingPaymentMethods(session?.athlete?.athleteId);

  const form = useForm<FormValues>({
    initialValues: {
      paymentMethod: paymentMethod,
    },
    validate: {
      paymentMethod: value => (!value ? 'Payment method is required' : null),
    },
  });

  const handleSavePaymentMethod = (): void => {
    setPaymentMethod(form.values.paymentMethod);
    setState('Review');
  };

  const handleAddPaymentMethod = (): void => {
    setState('Add Payment Method');
  };

  const handleCloseAndReset = (): void => {
    handleClose();
    form.reset();
  };

  return (
    <Stack>
      <Stack spacing='sm' px='xl'>
        <PaymentMethodSelect
          withinPortal
          required
          placeholder='Select a payment method'
          athleteId={session?.athlete?.athleteId}
          useDefaultPayment
          {...form.getInputProps('paymentMethod')}
        />
        <Button
          leftIcon={<IconPlus size={theme.fontSizes.sm} />}
          w='fit-content'
          onClick={handleAddPaymentMethod}
          variant='subtle'
        >
          Add Payment Method
        </Button>
        {isSharingPaymentMethods && <SharedPaymentMethodsAlert sx={{ marginBottom: '1rem' }} />}
      </Stack>
      <Group position='right'>
        <Button variant='subtle' onClick={handleCloseAndReset}>
          Cancel
        </Button>
        <Button type='submit' disabled={!form.values.paymentMethod} onClick={handleSavePaymentMethod}>
          Continue
        </Button>
      </Group>
    </Stack>
  );
};

export default SelectPaymentMethod;
